import { useEffect, useMemo } from 'react';
import type { TitleLevel } from '@course-authoring/shared-types';
import { useTheme } from '@course-authoring/shared-types';
import { createContentBlockStyles } from './styles/contentBlocks';

export interface QuestionBlockFallbackProps {
  title?: string;
  titleLevel?: TitleLevel;
  onInteractionComplete?: () => void;
}

/**
 * Learner-facing fallback for a question block with no question content
 * (no bank question selected, or an unresolvable reference). Renders the
 * block title only — nothing at all without one — and immediately reports
 * the interaction complete so the block never gates learner progress.
 */
export function QuestionBlockFallback({
  title,
  titleLevel = 'h2',
  onInteractionComplete,
}: QuestionBlockFallbackProps): React.ReactElement | null {
  const { tokens } = useTheme();
  const styles = useMemo(() => createContentBlockStyles(tokens), [tokens]);

  useEffect(() => {
    onInteractionComplete?.();
    // Intentionally mount-only: completion is a one-shot signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!title || title.trim().length === 0) {
    return null;
  }

  const HeadingTag = titleLevel;
  return (
    <div style={styles.container}>
      <HeadingTag style={styles[titleLevel]}>{title}</HeadingTag>
    </div>
  );
}

QuestionBlockFallback.displayName = 'QuestionBlockFallback';
